import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Steps } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import {
    NuevaAxiosURL,
    basename
} from "../../../utils/utils";
import MobileInput from "../../Mobile/Inputs/MobileInput";
import MobileDropdown from "../../Mobile/Inputs/MobileDropdown";
import MobileTextArea from "../../Mobile/Inputs/MobileTextArea";
import MobileButton from "../../Mobile/Buttons/MobileButton";
import BackButton from "../../ui/BackButton/BackButton";
import Checkbox from "../../ui/Checkbox/Checkbox";
import {
    ACTIVITIES,
    ADDITIONAL_SERVICES,
    TRAINER_OPTIONS,
    SPECIALTIES,
    AVAILABILITY_OPTIONS,
    METRICS
} from "./constants";
import "./CompleteProfile.css";
import "../Login/Login.css";

const CompleteProfile = ({ elevvaT, companyData: initialCompanyData }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const forceMembership = searchParams.get("force_membership") === "true";
    const forceData = searchParams.get("force_data") === "true";

    const [companyData, setCompanyData] = useState(initialCompanyData);
    const logoRef = useRef(null);
    const contentRef = useRef(null);
    const hasFilledRef = useRef(false);

    // --- State ---
    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Step 0: Basic Info
    const [logoPreview, setLogoPreview] = useState(companyData?.logo || null);
    const [logoFile, setLogoFile] = useState(null);
    const [selectedActivities, setSelectedActivities] = useState([]);
    const [otherActivityText, setOtherActivityText] = useState("");
    const [selectedCountry, setSelectedCountry] = useState(companyData?.country || "Colombia");
    const [countries, setCountries] = useState([]);
    const [selectedCity, setSelectedCity] = useState(companyData?.city || "");
    const [cities, setCities] = useState([]);
    const [address, setAddress] = useState(companyData?.address || "");
    const [branches, setBranches] = useState(companyData?.locations_number?.toString() || "");
    const [commercialName, setCommercialName] = useState("");

    // Legacy fields
    const [rutFile, setRutFile] = useState(null);
    const [camaraFile, setCamaraFile] = useState(null);
    const [termsAccepted, setTermsAccepted] = useState(false);

    // Step 1: Complementary Info
    const [selectedServices, setSelectedServices] = useState([]);
    const [otherServiceText, setOtherServiceText] = useState("");
    const [selectedTrainerCount, setSelectedTrainerCount] = useState(null);
    const [selectedSpecialties, setSelectedSpecialties] = useState([]);
    const [otherSpecialtyText, setOtherSpecialtyText] = useState("");
    const [availability, setAvailability] = useState([]);
    const [usesPlatform, setUsesPlatform] = useState(null);
    const [platformName, setPlatformName] = useState("");
    const [importantMetrics, setImportantMetrics] = useState([]);
    const [rewardsEnabled, setRewardsEnabled] = useState(null);

    // --- Effects ---
    useEffect(() => {
        // Fetch company data if missing but logged in
        const fetchCompany = async () => {
            const token = localStorage.my_code;
            if (!companyData && token) {
                try {
                    const res = await axios.get(`${NuevaAxiosURL}memberships/corp/company`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setCompanyData(res.data);
                } catch (err) {
                    console.error("Error fetching company for complete profile:", err);
                }
            }
        };
        fetchCompany();

        // Fetch countries and cities (using legacy paths)
        axios.get("/data/paises.json").then((res) => setCountries(res.data));
        axios.get("/data/colombia-cities.json")
            .then((res) => {
                setCities(res.data);
                // Pre-fill city for legacy data if needed
                if (companyData?.city && !selectedCity) {
                    const match = res.data.find(c => c.label.toLowerCase().includes(companyData.city.toLowerCase()));
                    if (match) setSelectedCity(match.label);
                }
            });
    }, [initialCompanyData]);

    // Update states when companyData is available
    useEffect(() => {
        if (companyData && !hasFilledRef.current) {
            if (companyData.logo) setLogoPreview(companyData.logo);
            if (companyData.country) setSelectedCountry(companyData.country);
            if (companyData.address) setAddress(companyData.address);
            if (companyData.locations_number) setBranches(companyData.locations_number.toString());

            if (companyData.city && cities.length > 0) {
                const match = cities.find(c => c.label.toLowerCase().includes(companyData.city.toLowerCase()));
                if (match) setSelectedCity(match.label);
            }

            // Pre-fill activities
            if (companyData.company_activities) {
                const parsed = companyData.company_activities.map(a => parseInt(a)).filter(a => !isNaN(a));
                if (parsed.length > 0) setSelectedActivities(parsed);
            }
            if (companyData.company_activities_others) {
                setOtherActivityText(companyData.company_activities_others);
            }

            // Pre-fill services
            if (companyData.additional_services) {
                const parsed = companyData.additional_services.map(s => parseInt(s)).filter(s => !isNaN(s));
                if (parsed.length > 0) setSelectedServices(parsed);
            }
            if (companyData.additional_services_others) {
                setOtherServiceText(companyData.additional_services_others);
            }

            // Pre-fill trainers and specialties
            if (companyData.trainers_count) {
                setSelectedTrainerCount(parseInt(companyData.trainers_count));
            }
            if (companyData.team_specialties) {
                const parsed = companyData.team_specialties.map(s => parseInt(s)).filter(s => !isNaN(s));
                if (parsed.length > 0) setSelectedSpecialties(parsed);
            }
            if (companyData.team_specialties_others) {
                setOtherSpecialtyText(companyData.team_specialties_others);
            }

            // Pre-fill availability and integration
            if (companyData.trainers_availability) {
                setAvailability([parseInt(companyData.trainers_availability)]);
            }
            if (companyData.data_integration !== undefined) {
                setUsesPlatform(companyData.data_integration ? "SI" : "NO");
            }
            if (companyData.data_integration_text) {
                setPlatformName(companyData.data_integration_text);
            }

            // Pre-fill metrics and rewards
            if (companyData.company_metrics) {
                const parsed = companyData.company_metrics.map(m => parseInt(m)).filter(m => !isNaN(m));
                if (parsed.length > 0) setImportantMetrics(parsed);
            }
            if (companyData.loyalty_rewards !== undefined) {
                setRewardsEnabled(companyData.loyalty_rewards ? "SI" : "NO");
            }

            // Pre-fill documentation status for legacy transition
            if (companyData.rut_document) setRutFile({ name: "RUT Cargado" });
            if (companyData.commerce_document) setCamaraFile({ name: "Cámara de Comercio Cargada" });
            if (companyData.terms_accepted) setTermsAccepted(true);

            hasFilledRef.current = true;
        }
    }, [companyData, cities]);

    // --- Helpers ---
    const toggleItem = (value, state, setState) => {
        setState(prev =>
            prev.includes(value)
                ? prev.filter(i => i !== value)
                : [...prev, value]
        );
    };

    const isStep0Valid = () => {
        // Common required fields
        const commonValid = logoPreview &&
            selectedActivities.length > 0 &&
            selectedCountry &&
            address &&
            branches;

        if (!commonValid) return false;
        if (selectedCountry === "Colombia" && !selectedCity) return false;
        if (selectedActivities.includes(190) && !otherActivityText.trim()) return false;

        // Force Flags logic:
        // is_force_onboarding (old user): companyData.id exists, but needs RUT/Camara/Terms
        // If we are forcing ONLY data (and not membership), we don't need the commercial name
        if (forceData && !forceMembership) {
            return true;
        }

        // For all other cases (forceMembership=true OR normal flow for new/legacy users)
        // commercialName is mandatory. Legacy users also need documents.
        if (companyData?.old_company && !forceMembership && !forceData) {
            return !!(commercialName && rutFile && camaraFile && termsAccepted);
        } else {
            return !!commercialName;
        }
    };

    const handleNext = () => {
        setStep(prev => prev + 1);
        contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleBack = () => {
        if (step > 0) {
            setStep(prev => prev - 1);
            contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            navigate(-1);
        }
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const formData = new FormData();
            if (logoFile) formData.append("logo", logoFile);

            formData.append("country", selectedCountry);
            formData.append("city", selectedCity);
            formData.append("address", address);
            formData.append("locations_number", branches);
            formData.append("loyalty_rewards", rewardsEnabled === "SI");
            formData.append("data_integration", usesPlatform === "SI");

            if (!forceData || forceMembership) {
                formData.append("commercial_name", commercialName);
            }

            if (companyData?.old_company && !forceMembership && !forceData) {
                // Documents for legacy transition ONLY (old users)
                if (rutFile instanceof File) formData.append("rut_document", rutFile);
                if (camaraFile instanceof File) formData.append("commerce_document", camaraFile);
                if (termsAccepted) formData.append("terms_accepted", "true");
            }

            if (selectedActivities.includes(190)) formData.append("company_activities_others", otherActivityText);
            if (selectedServices.includes(80)) formData.append("additional_services_others", otherServiceText);
            if (selectedSpecialties.includes(100)) formData.append("team_specialties_others", otherSpecialtyText);
            if (usesPlatform === "SI") formData.append("data_integration_text", platformName);

            selectedActivities.forEach(v => formData.append("company_activities", v));
            selectedServices.forEach(v => formData.append("additional_services", v));
            importantMetrics.forEach(v => formData.append("company_metrics", v));
            selectedSpecialties.forEach(v => formData.append("team_specialties", v));
            availability.forEach(v => formData.append("trainers_availability", v));
            if (selectedTrainerCount) formData.append("trainers_count", selectedTrainerCount);

            const accessToken = localStorage.my_code;
            const pk = companyData ? companyData.id : localStorage.company_id;

            // Choose correct endpoint
            const url = (companyData?.old_company && !forceMembership && !forceData)
                ? `${NuevaAxiosURL}customers/corp/company/${pk}/update/old`
                : `${NuevaAxiosURL}customers/corp/company/${pk}/update`;

            const res = await axios.patch(url, formData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "multipart/form-data",
                }
            });

            if (res.data.company_id) {
                const d = res.data.company_id;
                localStorage.setItem("mob_api_key", d.mob_api_key);
                localStorage.setItem("mob_integration", d.mob_integration);
                localStorage.setItem("mob_currency", d.currency);
            }

            setStep(2); // Success step
        } catch (error) {
            console.error("Submission error:", error);
            alert("Hubo un problema al enviar tu información. Intenta nuevamente.");
            setIsSubmitting(false);
        }
    };

    // --- Renderers ---
    const renderStep0 = () => (
        <div className="animate-fade-in-up w-100 d-flex flex-column gap-3">
            <h2 className="profile-step-title">Información básica</h2>

            {/* Logo Upload */}
            <div
                className="profile-logo-uploader mx-auto mb-2"
                onClick={() => logoRef.current?.click()}
            >
                {logoPreview ? (
                    <img src={logoPreview} alt="Logo Preview" />
                ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <i className="bi bi-cloud-arrow-up fs-2 mb-1"></i>
                        <span>Logo del centro</span>
                    </div>
                )}
                <input
                    type="file"
                    ref={logoRef}
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                            setLogoFile(file);
                            setLogoPreview(URL.createObjectURL(file));
                        }
                    }}
                />
            </div>

            {/* Activities Select */}
            <div className="w-100 mb-2">
                <label className="profile-label">Actividades (Selección múltiple)</label>
                <div className="profile-multi-select">
                    <div className="row g-2">
                        {ACTIVITIES.map(a => (
                            <div key={a.value} className="col-6">
                                <div
                                    className={`profile-activity-tag ${selectedActivities.includes(a.value) ? 'active' : ''}`}
                                    onClick={() => toggleItem(a.value, selectedActivities, setSelectedActivities)}
                                >
                                    {a.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {selectedActivities.includes(190) && (
                    <div className="mt-2">
                        <MobileInput
                            value={otherActivityText}
                            onChange={e => setOtherActivityText(e.target.value)}
                            placeholder="Especifique otra actividad"
                        />
                    </div>
                )}
            </div>

            <div className="profile-form-grid">
                {(!forceData || forceMembership) && (
                    <div className="profile-input-group">
                        <MobileInput
                            label="Nombre comercial"
                            value={commercialName}
                            onChange={e => setCommercialName(e.target.value)}
                            placeholder="Ej: Elevva Training Center"
                        />
                    </div>
                )}

                <div className="profile-input-group">
                    <label className="profile-label">Ubicación</label>
                    <div className="d-flex gap-3">
                        <div className="w-50">
                            <MobileDropdown
                                options={countries.map(c => ({ value: c.name, label: c.name }))}
                                value={selectedCountry}
                                onChange={val => setSelectedCountry(val)}
                                placeholder="País"
                                searchable={true}
                            />
                        </div>
                        <div className="w-50">
                            <MobileDropdown
                                options={cities.map(c => ({ value: c.label, label: c.label }))}
                                value={selectedCity}
                                onChange={val => setSelectedCity(val)}
                                placeholder="Ciudad"
                                disabled={selectedCountry !== "Colombia"}
                                searchable={true}
                            />
                        </div>
                    </div>
                </div>

                <div className="profile-input-group">
                    <MobileInput
                        label="Dirección física"
                        value={address}
                        onChange={e => setAddress(e.target.value)}
                        placeholder="Dirección física"
                    />
                </div>

                <div className="profile-input-group">
                    <MobileInput
                        label="Número de sedes"
                        type="number"
                        value={branches}
                        onChange={e => setBranches(e.target.value)}
                        placeholder="Número de sedes"
                    />
                </div>

                {companyData?.old_company && !forceMembership && !forceData && (
                    <>
                        <div className="profile-input-group">
                            <MobileInput
                                type="file"
                                label="RUT (PDF o Imagen)"
                                placeholder="Seleccionar archivo"
                                value={rutFile}
                                accept=".pdf,.png,.jpg"
                                onChange={e => setRutFile(e.target.files[0])}
                            />
                        </div>
                        <div className="profile-input-group">
                            <MobileInput
                                type="file"
                                label="Cámara de Comercio"
                                placeholder="Seleccionar archivo"
                                value={camaraFile}
                                accept=".pdf,.png,.jpg"
                                onChange={e => setCamaraFile(e.target.files[0])}
                            />
                        </div>
                        <div className="col-12 mt-2">
                            <Checkbox
                                checked={termsAccepted}
                                onChange={() => setTermsAccepted(!termsAccepted)}
                                label={
                                    <span>
                                        Acepto los <a href="#" onClick={e => e.preventDefault()}>términos y condiciones</a> y la <a href="#" onClick={e => e.preventDefault()}>política de privacidad</a>.
                                    </span>
                                }
                            />
                        </div>
                    </>
                )}
            </div>

            <div className="pt-3">
                <MobileButton
                    text="Continuar"
                    disabled={!isStep0Valid()}
                    onClick={handleNext}
                />
            </div>
        </div>
    );

    const renderCircularSelection = (title, items, state, setState, isMulti = true, path = "assets/icons-services") => (
        <div className="animate-fade-in-up w-100 mb-1">
            <h3 className="profile-section-subtitle">{title}</h3>
            <div className="profile-circular-grid">
                {items.map(item => {
                    const isSelected = isMulti ? state.includes(item.value) : state === item.value;
                    return (
                        <div
                            key={item.value}
                            className="profile-circular-item"
                            onClick={() => isMulti ? toggleItem(item.value, state, setState) : setState(item.value)}
                        >
                            <div className={`profile-icon-circle ${isSelected ? 'active' : ''}`}>
                                {item.iconIndex ? (
                                    <img
                                        src={`/${path}/${item.iconIndex}.svg`}
                                        alt={item.name}
                                        className={isSelected ? 'invert' : ''}
                                    />
                                ) : (
                                    <span>{item.label}</span>
                                )}
                            </div>
                            <span className="profile-icon-label">{item.label || item.name}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const renderCardSelection = (title, items, state, setState, isMulti = true) => (
        <div className="animate-fade-in-up w-100 mb-1">
            <h3 className="profile-section-subtitle">{title}</h3>
            <div className="profile-card-grid">
                {items.map(item => {
                    const isSelected = isMulti ? state.includes(item.value) : state === item.value;
                    return (
                        <div
                            key={item.value}
                            className={`profile-option-card ${isSelected ? 'active' : ''}`}
                            onClick={() => isMulti ? toggleItem(item.value, state, setState) : setState(item.value)}
                        >
                            <span className="card-label">{item.label}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const renderStep1 = () => (
        <div className="animate-fade-in-up w-100 d-flex flex-column gap-3">
            <h2 className="profile-step-title">Información complementaria</h2>

            {renderCircularSelection("Servicios adicionales", ADDITIONAL_SERVICES, selectedServices, setSelectedServices)}
            {selectedServices.includes(80) && (
                <MobileTextArea
                    placeholder="Cuéntanos más sobre tus servicios..."
                    value={otherServiceText}
                    onChange={e => setOtherServiceText(e.target.value)}
                />
            )}

            {renderCardSelection("Cantidad de entrenadores", TRAINER_OPTIONS, selectedTrainerCount, setSelectedTrainerCount, false)}

            {renderCircularSelection("Especialidades del equipo", SPECIALTIES, selectedSpecialties, setSelectedSpecialties)}
            {selectedSpecialties.includes(100) && (
                <MobileTextArea
                    placeholder="Otras especialidades..."
                    value={otherSpecialtyText}
                    onChange={e => setOtherSpecialtyText(e.target.value)}
                />
            )}

            {renderCircularSelection("Disponibilidad", AVAILABILITY_OPTIONS, availability, setAvailability)}

            <div className="profile-toggle-section">
                <p className="profile-label fw-bold">¿Usa alguna plataforma de gestión?</p>
                <div className="d-flex gap-2">
                    {['SI', 'NO'].map(opt => (
                        <button
                            key={opt}
                            className={`profile-toggle-btn ${usesPlatform === opt ? 'active' : ''}`}
                            onClick={() => setUsesPlatform(opt)}
                        >
                            {opt}
                        </button>
                    ))}
                </div>
                {usesPlatform === "SI" && (
                    <div className="mt-3 animate-fade-in-up">
                        <MobileInput
                            placeholder="Nombre de la plataforma"
                            value={platformName}
                            onChange={e => setPlatformName(e.target.value)}
                        />
                    </div>
                )}
            </div>

            {renderCircularSelection("Métricas importantes", METRICS, importantMetrics, setImportantMetrics)}

            <div className="profile-toggle-section">
                <p className="profile-label fw-bold">¿Habilitar programas de fidelización?</p>
                <div className="d-flex gap-2">
                    {['SI', 'NO'].map(opt => (
                        <button
                            key={opt}
                            className={`profile-toggle-btn ${rewardsEnabled === opt ? 'active' : ''}`}
                            onClick={() => setRewardsEnabled(opt)}
                        >
                            {opt}
                        </button>
                    ))}
                </div>
            </div>

            <div className="pt-3">
                <MobileButton
                    text={isSubmitting ? "Enviando..." : "Finalizar"}
                    onClick={handleSubmit}
                    loading={isSubmitting}
                />
            </div>
        </div>
    );

    const renderSuccess = () => (
        <div className="animate-fade-in-up w-100 text-center py-4">
            <div className="profile-success-icon mx-auto mb-4">
                <i className="bi bi-check-lg"></i>
            </div>
            <h1 className="profile-step-title mb-3">¡Perfil creado con éxito!</h1>
            <p className="mobile-login-description mb-5">
                Es momento de transformar la experiencia de entrenamiento con <strong>Elevva</strong>.
            </p>
            <MobileButton
                text="Ir al Dashboard"
                onClick={() => window.location.href = `${basename}/dashboard/home`}
            />
        </div>
    );

    return (
        <div className="mobile-login-body-inner justify-content-start" ref={contentRef}>
            <div className="d-flex flex-column align-items-center w-100">

                {/* Header Branding */}
                <div className="w-100 mb-4 animate-fade-in-up">
                    <div className="auth-header-flex">
                        {step < 2 ? (
                            <div className="auth-nav-area">
                                <BackButton onClick={handleBack} />
                            </div>
                        ) : (
                            <div className="auth-nav-area hidden"></div>
                        )}
                        <h1 className="auth-main-title">Complete su Perfil</h1>
                        <div className="auth-nav-area hidden"></div>
                    </div>
                    <div className="recover-accent-line mx-auto"></div>
                </div>

                {/* Progress Tracker */}
                {step < 2 && (
                    <div className="w-100 mb-4 px-4">
                        <Steps current={step} className="custom-steps">
                            <Steps.Item />
                            <Steps.Item />
                        </Steps>
                    </div>
                )}

                {/* Step Content */}
                <div className="w-100">
                    {step === 0 && renderStep0()}
                    {step === 1 && renderStep1()}
                    {step === 2 && renderSuccess()}
                </div>

                {/* Footer Decor */}
                <div className="mt-5 pt-4 text-center">
                    <p className="welcome-footer-tagline">Impulsado por Elevva Intelligence</p>
                </div>
            </div>
        </div>
    );
};

export default CompleteProfile;
